import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Message } from 'semantic-ui-react';
import SimplifiedRecordingControls from '../../../utils/SimplifiedRecordingControls';
import { v4 as uuidv4 } from 'uuid';
import {
  setTargetData,
  setSnippetDetails,
  clearRecordingState,
  selectRecordingStatus,
  selectLastRecordingUrl
} from '../../../slices/aaiRecordingUtilitySlice';

const SimpleRecordVariation = ({
  isActive = false,
  onClose,
  selectedBook,
  selectedCharacter,
  selectedVariation,
  contentText,
  onSaveRecording,
  newVariationId,
  onRecordingStatusChange // New prop for tracking recording status
}) => {
  const dispatch = useDispatch();
  const recordingStatus = useSelector(selectRecordingStatus);
  const lastRecordingUrl = useSelector(selectLastRecordingUrl);
  // Get the userId from Redux state at the top level of the component
  const userId = useSelector(state => state.user.profile?.data?.userId) || 'unknown';
  // Use ref to store the variation ID
  const activeVariationId = useRef(newVariationId);

  // Update the ref value when newVariationId changes
  useEffect(() => {
    activeVariationId.current = newVariationId;
  }, [newVariationId]);

  // Generate a UUID when the component becomes active for a new variation
  useEffect(() => {
    if (isActive) {
      // If we're creating a new variation (no selectedVariation), use the newVariationId
      if (!selectedVariation || !selectedVariation.uuid) {
        activeVariationId.current = selectedVariation?.uuid || newVariationId;
      } else {
        // If editing, use the existing UUID
        activeVariationId.current = selectedVariation.uuid;
      }
    }
  }, [isActive, selectedVariation, newVariationId]);

  // Effect to notify parent component of recording status changes
  useEffect(() => {
    if (onRecordingStatusChange) {
      onRecordingStatusChange(recordingStatus);
    }
  }, [recordingStatus, onRecordingStatusChange]);

  // Prepare voice detail object for the recording controls - memoized to prevent re-renders
  const voiceDetailObj = React.useMemo(() => ({
    litGroupItem: { name: 'Book' },
    bookItem: {
      name: selectedBook?.name || 'Default Book',
      uuid: selectedBook?.uuid || 'default-book'
    },
    characterItem: {
      name: selectedCharacter?.name || 'Default Character',
      uuid: selectedCharacter?.uuid || 'default-character'
    },
    bookId: selectedBook?.uuid || 'default-book',
    characterId: selectedCharacter?.uuid || 'default-character',
    variationId: selectedVariation?.uuid || activeVariationId.current || 'default-variation'
  }), [
    selectedBook,
    selectedCharacter,
    selectedVariation,
    activeVariationId
  ]);

  // Prepare target data for the recording controls
  useEffect(() => {
    if (isActive && selectedCharacter) {
      // Get the content text to use
      const textToUse = contentText || selectedCharacter.description || 'No description available';

      // Initialize recording state when modal opens
      const targetData = {
        character: {
          character: {
            character: {
              uuid: selectedCharacter.uuid,
              name: selectedCharacter.name
            }
          },
          variation: selectedVariation?.name || 'Default'
        },
        variation: selectedVariation?.name || 'Default',
        textSnippet: {
          content: textToUse
        }
      };

      // Set snippet details with a more robust structure
      const snippetDetails = {
        details: {
          values: {
            literaryGroup: 'books',
            litGroupBook: selectedBook?.uuid || 'default-book'
          }
        },
        snippetValues: {
          values: {
            title: `${selectedCharacter.name} - ${selectedVariation?.name || 'Default'}`,
            enabled: true
          }
        }
      };

      // Dispatch actions to set up recording state
      dispatch(clearRecordingState());
      dispatch(setTargetData(targetData));
      dispatch(setSnippetDetails(snippetDetails));
    }
  }, [isActive, dispatch, selectedCharacter, selectedVariation, selectedBook, contentText]);

  // State to store the recorded audio blob
  const [recordedAudioBlob, setRecordedAudioBlob] = useState(null);

  // Effect to capture the audio blob when recording is completed or stopped
  useEffect(() => {
    if ((recordingStatus === 'completed' || recordingStatus === 'stopped') &&
      window.audioChunksRef && window.audioChunksRef.length > 0) {
      // Create a blob from the audio chunks
      const audioBlob = new Blob(window.audioChunksRef, { type: "audio/wav" });
      setRecordedAudioBlob(audioBlob);
    }
  }, [recordingStatus]);

  // State for save status
  const [saveStatus, setSaveStatus] = useState({
    saving: false,
    success: false,
    error: null
  });

  // Function to calculate audio duration from blob using Web Audio API with fallback
  const calculateAudioDuration = useCallback((audioBlob) => {
    return new Promise((resolve) => {
      // Try using Web Audio API first (more reliable)
      if (window.AudioContext || window.webkitAudioContext) {
        const fileReader = new FileReader();

        fileReader.onload = function (event) {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();

          audioContext.decodeAudioData(event.target.result,
            // Success callback
            function (buffer) {
              const duration = buffer.duration;
              resolve(duration);
            },
            // Error callback
            function (error) {
              console.error('Error decoding audio data:', error);
              // Fall back to HTML5 Audio element approach
              fallbackToHtmlAudio();
            }
          );
        };

        fileReader.onerror = function () {
          console.error('Error reading file');
          // Fall back to HTML5 Audio element approach
          fallbackToHtmlAudio();
        };

        fileReader.readAsArrayBuffer(audioBlob);
      } else {
        // Fall back to HTML5 Audio element approach if Web Audio API is not available
        fallbackToHtmlAudio();
      }

      // HTML5 Audio element fallback approach
      function fallbackToHtmlAudio() {
        // Create a persistent reference to the blob URL
        const blobUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio();

        // Set up multiple event listeners for better reliability
        const events = ['loadedmetadata', 'canplay', 'canplaythrough'];

        const handleDurationAvailable = () => {
          if (audio.duration && isFinite(audio.duration)) {
            // Clean up event listeners
            events.forEach(event => audio.removeEventListener(event, handleDurationAvailable));

            // Revoke the blob URL to prevent memory leaks
            URL.revokeObjectURL(blobUrl);

            resolve(audio.duration);
          }
        };

        // Add multiple event listeners
        events.forEach(event => audio.addEventListener(event, handleDurationAvailable));

        // Handle errors
        audio.addEventListener('error', () => {
          console.error('Error loading audio for duration calculation');
          URL.revokeObjectURL(blobUrl);
          resolve(30); // Default fallback
        });

        // Set source and explicitly load
        audio.src = blobUrl;
        audio.preload = 'metadata'; // Only load metadata, not the entire file
        audio.load();

        // Fallback timeout - longer duration for larger files
        setTimeout(() => {
          if (audio.duration && isFinite(audio.duration)) {
            resolve(audio.duration);
          } else {
            resolve(30); // Default fallback
          }

          // Clean up
          events.forEach(event => audio.removeEventListener(event, handleDurationAvailable));
          URL.revokeObjectURL(blobUrl);
        }, 5000);
      }
    });
  }, []);

  // Handle recording status change
  const handleRecordingStatusChange = useCallback(async (status, audioChunks) => {
    if (status === 'completed' || status === 'stopped') {
      if (audioChunks && audioChunks.length > 0) {
        // Create a blob from the audio chunks
        const audioBlob = new Blob(audioChunks, { type: "audio/wav" });

        // Calculate audio duration
        let fileDuration = 0;
        try {
          fileDuration = await calculateAudioDuration(audioBlob);
        } catch (error) {
          console.error('Error calculating audio duration:', error);
        }

        // Get the S3 URL from the Redux state - using userId from the component level
        let s3Url = lastRecordingUrl || `s3://temp-path/${userId}/books/${selectedBook?.uuid || 'book'}/characters/${selectedCharacter?.uuid || 'character'}/variations/${selectedVariation?.name || 'new-variation'}_temp.wav`;

        // Create metadata with detailed information
        const metadata = {
          bookId: selectedBook?.uuid,
          characterId: selectedCharacter?.uuid,
          variationId: selectedVariation?.uuid || activeVariationId.current || uuidv4(),
          variationName: selectedVariation?.name || 'New Variation',
          isEditing: selectedVariation && selectedVariation.uuid,
          fileDuration: fileDuration
        };

        // Pass the recording data to the parent component
        onSaveRecording({
          s3Url,
          audioBlob,
          metadata
        });

        // Set success status
        setSaveStatus({ saving: false, success: true, error: null });
      }
    }
  }, [
    lastRecordingUrl,
    selectedBook,
    selectedCharacter,
    selectedVariation,
    activeVariationId,
    calculateAudioDuration,
    onSaveRecording,
    userId // Add userId to the dependency array
  ]);
  //style={{ display: isActive ? 'block' : 'none' }}
  return (
    <div className="simple-record-variation" >
      <div className="record-variation-content">
        {/* Recording Container */}
        <div className="recording-container">
          <SimplifiedRecordingControls
            voiceDetailObj={voiceDetailObj}
            contentText={contentText || selectedCharacter?.description || 'No content available for recording.'}
            hideControls={true}
            onStatusChange={handleRecordingStatusChange}
            customStyles={{
              container: { backgroundColor: '#f0f7fa' }
            }}
          />
          <div style={{ paddingBottom: '5px' }}>
            {/* Status message */}
            {saveStatus.saving && (
              <Message info>
                <Message.Header>Saving Recording</Message.Header>
                <p>Please wait while your recording is being saved...</p>
              </Message>
            )}

            {saveStatus.success && (
              <Message positive >
                <Message.Header>Recording Captured</Message.Header>
                <p>Your recording has been captured successfully! Click Create/Update button...</p>
              </Message>
            )}

            {saveStatus.error && (
              <Message negative>
                <Message.Header>Error Saving Recording</Message.Header>
                <p>{saveStatus.error}</p>
              </Message>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Use React.memo to prevent unnecessary re-renders
export default React.memo(SimpleRecordVariation, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.isActive === nextProps.isActive &&
    prevProps.contentText === nextProps.contentText &&
    prevProps.selectedCharacter?.uuid === nextProps.selectedCharacter?.uuid &&
    prevProps.selectedVariation?.uuid === nextProps.selectedVariation?.uuid &&
    prevProps.newVariationId === nextProps.newVariationId &&
    prevProps.onRecordingStatusChange === nextProps.onRecordingStatusChange
  );
});
